import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { FC, useState } from 'react';
import { toast } from 'react-hot-toast';

import { CardTitleWithOptions } from '../../../components/cardTitleWithOptions/CardTitleWithOptions';
import { useAppDispatch, useAppSelector } from '../../../state/redux.hooks';
import {
  useGenerate2faMutation,
  useEnable2faMutation,
  useDisable2faMutation,
} from '../../auth/api/authApi';
import { selectMitarbeiterId } from '../../auth/api/authSlice';
import { mitarbeiterApi } from '../api/mitarbeiterApi';
import { Mitarbeiter } from '../api/types';

export const MitarbeiterSecurityCard: FC<{ mitarbeiter: Mitarbeiter }> = ({ mitarbeiter }) => {
  const dispatch = useAppDispatch();
  const loggedInMitarbeiterId = useAppSelector(selectMitarbeiterId);
  const isSelf = mitarbeiter.id === loggedInMitarbeiterId;

  const [generate2fa, { isLoading: isGenerating }] = useGenerate2faMutation();
  const [enable2fa, { isLoading: isEnabling }] = useEnable2faMutation();
  const [disable2fa, { isLoading: isDisabling }] = useDisable2faMutation();

  const [setupString, setSetupString] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [isDisableMode, setIsDisableMode] = useState(false);

  const handleCopySecret = async () => {
    if (setupString) {
      try {
        await navigator.clipboard.writeText(setupString);
        toast.success('Geheimschlüssel kopiert');
      } catch {
        toast.error('Kopieren fehlgeschlagen');
      }
    }
  };

  const handleEnableClick = async () => {
    try {
      const res = await generate2fa().unwrap();
      setSetupString(res.setupString);
      setIsSetupMode(true);
      setIsDisableMode(false);
      setCode('');
    } catch (err: unknown) {
      console.error(err);
      toast.error('Fehler beim Generieren des 2FA-Geheimnisses');
    }
  };

  const handleVerify = async () => {
    const cleanCode = code.replace(/\D/g, '').slice(0, 6);
    if (!cleanCode || cleanCode.length !== 6) {
      toast.error('Bitte geben Sie einen 6-stelligen Code ein');
      return;
    }
    try {
      await enable2fa({ code: cleanCode }).unwrap();
      dispatch(
        mitarbeiterApi.util.invalidateTags([
          { type: 'Mitarbeiter', id: mitarbeiter.id },
          { type: 'Mitarbeiter', id: 'LIST' },
          { type: 'Mitarbeiter', id: 'aktiv' },
        ])
      );
      toast.success('2FA erfolgreich aktiviert');
      setIsSetupMode(false);
      setSetupString(null);
      setCode('');
    } catch (err: unknown) {
      console.error(err);
      const apiErr = err as { status?: number; data?: { message?: string | string[] } };
      if (apiErr?.status === 429) {
        toast.error(
          'Zu viele Versuche. Bitte warten Sie 5 Minuten, bevor Sie es erneut versuchen.'
        );
      } else {
        const msg = Array.isArray(apiErr?.data?.message)
          ? apiErr.data.message[0]
          : apiErr?.data?.message || 'Ungültiger Code';
        toast.error(msg);
      }
    }
  };

  const handleDisable = async () => {
    const cleanCode = code.replace(/\D/g, '').slice(0, 6);
    if (!cleanCode || cleanCode.length !== 6) {
      toast.error('Bitte geben Sie einen 6-stelligen Code ein');
      return;
    }
    try {
      await disable2fa({ code: cleanCode }).unwrap();
      dispatch(
        mitarbeiterApi.util.invalidateTags([
          { type: 'Mitarbeiter', id: mitarbeiter.id },
          { type: 'Mitarbeiter', id: 'LIST' },
          { type: 'Mitarbeiter', id: 'aktiv' },
        ])
      );
      toast.success('2FA erfolgreich deaktiviert');
      setIsDisableMode(false);
      setCode('');
    } catch (err: unknown) {
      console.error(err);
      const apiErr = err as { status?: number; data?: { message?: string | string[] } };
      if (apiErr?.status === 429) {
        toast.error(
          'Zu viele Versuche. Bitte warten Sie 5 Minuten, bevor Sie es erneut versuchen.'
        );
      } else {
        const msg = Array.isArray(apiErr?.data?.message)
          ? apiErr.data.message[0]
          : apiErr?.data?.message || 'Ungültiger Code';
        toast.error(msg);
      }
    }
  };

  const otpauthUrl = setupString
    ? `otpauth://totp/Auriscon:${encodeURIComponent(mitarbeiter.email)}?secret=${encodeURIComponent(setupString)}&issuer=Auriscon`
    : '';

  return (
    <Card>
      <CardContent>
        <CardTitleWithOptions title="Sicherheit" />
        <Stack spacing={3} sx={{ mt: 2 }}>
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Zwei-Faktor-Authentifizierung (2FA)
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Status: <strong>{mitarbeiter.isTotpEnabled ? 'Aktiviert' : 'Deaktiviert'}</strong>
            </Typography>
          </Box>

          {!mitarbeiter.isTotpEnabled && !isSetupMode && isSelf && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleEnableClick}
              disabled={isGenerating}
            >
              2FA Aktivieren
            </Button>
          )}

          {isSetupMode && setupString && (
            <Stack spacing={2}>
              <Alert severity="info">
                Scannen Sie den QR-Code mit einer Authenticator-App (z.B. Google Authenticator oder
                Microsoft Authenticator).
              </Alert>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                }}
              >
                <QRCodeSVG value={otpauthUrl} size={200} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="body2">
                  Oder geben Sie den Code manuell ein: <strong>{setupString}</strong>
                </Typography>
                <Tooltip title="Geheimschlüssel kopieren">
                  <IconButton
                    size="small"
                    onClick={handleCopySecret}
                    aria-label="Geheimschlüssel kopieren"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                label="6-stelliger Bestätigungscode"
                value={code}
                slotProps={{
                  htmlInput: {
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 6,
                  },
                }}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                fullWidth
              />
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleVerify}
                  disabled={isEnabling || code.replace(/\D/g, '').length !== 6}
                  sx={{ flex: 1 }}
                >
                  Aktivieren
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsSetupMode(false);
                    setSetupString(null);
                    setCode('');
                  }}
                  sx={{ flex: 1 }}
                >
                  Abbrechen
                </Button>
              </Stack>
            </Stack>
          )}

          {mitarbeiter.isTotpEnabled && !isDisableMode && isSelf && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setIsDisableMode(true);
                setIsSetupMode(false);
                setCode('');
              }}
            >
              2FA Deaktivieren
            </Button>
          )}

          {isDisableMode && (
            <Stack spacing={2}>
              <Typography variant="body2">
                Geben Sie einen aktuellen 2FA-Code ein, um die Zwei-Faktor-Authentifizierung zu
                deaktivieren.
              </Typography>
              <TextField
                label="6-stelliger 2FA Code"
                value={code}
                slotProps={{
                  htmlInput: {
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 6,
                  },
                }}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                fullWidth
              />
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDisable}
                  disabled={isDisabling || code.replace(/\D/g, '').length !== 6}
                  sx={{ flex: 1 }}
                >
                  Deaktivieren
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsDisableMode(false);
                    setCode('');
                  }}
                  sx={{ flex: 1 }}
                >
                  Abbrechen
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
